import React from "react";
import type {
  BlockCategory,
  BlockSelection,
  Speaker,
  TextCustomizations,
  Vendor,
} from "src/types";
import { BlockVariant } from "src/types";

import Button from "@/components/editor/primitives/button/button-component";
import Image from "@/components/editor/primitives/image/image-component";
import Text from "@/components/editor/primitives/text/text-component";
import Video from "@/components/editor/primitives/video/video-component";
import {
  DEFAULT_BRONZE_SPONSORS,
  DEFAULT_GOLDEN_SPONSORS,
  DEFAULT_SCHEDULES,
  DEFAULT_SILVER_SPONSORS,
} from "@/components/editor/shared/constants";
import iframePreview from "@/components/editor/shared/media/iframe/assets/preview.jpg";
import TopLevelIFrame from "@/components/editor/shared/media/iframe/iframe-component";
import VideoGridPreview from "@/components/editor/shared/media/video-grid/assets/preview.png";
import VideoGrid from "@/components/editor/shared/media/video-grid/video-grid-component";
import { VIDEO_AREA_SIZE_SMALL_TYPE } from "@/components/editor/shared/media/video-with-title-settings";
import {
  DEFAULT_NAVIGATION_LINKS,
  getCurrentSelectionLinks,
} from "@/components/editor/shared/navigation";
import embeddedSchedulePreview from "@/components/editor/shared/schedules/embedded-schedule/assets/preview.jpg";
import EmbeddedSchedule from "@/components/editor/shared/schedules/embedded-schedule/embedded-schedule-component";
import Benefits from "@/components/editor/shared/sections/benefits/benefits-component";
import { DEFAULT_BENEFIT_CARDS } from "@/components/editor/shared/sections/benefits/constants";
import { DEFAULT_VENDORS } from "@/components/editor/themes/hazel/blocks/expo/constants";
import { sponsorWidgetList } from "@/components/editor/themes/utils/sponsor-widget-list";
import getInitialItemsOrDefault from "@/utils/get-initial-items-or-default";
import getTopLevelNodeTrees from "@/utils/get-top-level-node-trees";

import relateEventsPreview from "../../shared/sections/related-events/assets/preview.jpg";
import expoPreview from "./blocks/expo/assets/preview.jpg";
import BoothCard from "./blocks/expo/booth-card/booth-card-component";
import Booths from "./blocks/expo/booths-component";
import SyncedBooths from "./blocks/expo/synced-booths-component";
import headerOption1Preview from "./blocks/headers/header-option-1/assets/preview.jpg";
import HeaderOption1 from "./blocks/headers/header-option-1/header-option-1-component";
import headerOption2Preview from "./blocks/headers/header-option-2/assets/preview.jpg";
import HeaderOption2 from "./blocks/headers/header-option-2/header-option-2-component";
import headerOption3Preview from "./blocks/headers/header-option-3/assets/preview.jpg";
import HeaderOption3 from "./blocks/headers/header-option-3/header-option-3-component";
import videoWithParagraphPreview from "./blocks/media/video-with-paragraph/assets/preview.jpg";
import VideoWithParagraph from "./blocks/media/video-with-paragraph/video-with-paragraph-component";
import videoWithTitlePreview from "./blocks/media/video-with-title/assets/preview.jpg";
import VideoWithTitle from "./blocks/media/video-with-title/video-with-title-component";
import standaloneNavigationPreview from "./blocks/navigations/standalone-navigation/assets/preview.jpg";
import StandaloneNavigation from "./blocks/navigations/standalone-navigation/standalone-navigation-component";
import DefaultSchedule from "./blocks/schedules/default-schedule/default-schedule-component";
import schedulePreview from "./blocks/schedules/default-schedule/preview.jpg";
import SyncedDefaultSchedule from "./blocks/schedules/default-schedule/synced-default-schedule-component";
import AboutDetails from "./blocks/sections/about-details/about-details-component";
import aboutDetailsPreview from "./blocks/sections/about-details/assets/preview.jpg";
import benefitsPreview from "./blocks/sections/benefits/assets/preview.jpg";
import BenefitCard from "./blocks/sections/benefits/benefit-card/benefit-card-component";
import dividerPicturePreview from "./blocks/sections/divider-picture/assets/preview.jpg";
import DividerPicture from "./blocks/sections/divider-picture/divider-picture-component";
import Gallery from "./blocks/sections/gallery";
import galleryPreview from "./blocks/sections/gallery/assets/preview.jpg";
import paragraphPicturePreview from "./blocks/sections/paragraph-picture/assets/preview.jpg";
import paragraphPictureCTAPreview from "./blocks/sections/paragraph-picture/assets/previewCTA.jpg";
import ParagraphItem from "./blocks/sections/paragraph-picture/paragraph-item-component";
import ParagraphPicture from "./blocks/sections/paragraph-picture/paragraph-picture-component";
import RelatedEventsSection from "./blocks/sections/related-events/";
import richTextPreview from "./blocks/sections/rich-text-section/assets/preview.jpg";
import RichTextSection from "./blocks/sections/rich-text-section/rich-text-section-component";
import speakersPreview from "./blocks/speakers/assets/preview.jpg";
import { DEFAULT_SPEAKERS } from "./blocks/speakers/constants";
import SpeakerCard from "./blocks/speakers/speaker-card/speaker-card-component";
import Speakers from "./blocks/speakers/speakers-component";
import SyncedSpeakers from "./blocks/speakers/synced-speakers-component";
import sponsorsBronzePreview from "./blocks/sponsors/assets/preview-bronze.jpg";
import sponsorsGoldenPreview from "./blocks/sponsors/assets/preview-golden.jpg";
import sponsorsSilverPreview from "./blocks/sponsors/assets/preview-silver.jpg";
import SponsorCard from "./blocks/sponsors/sponsor-card/sponsor-card-component";
import Sponsors from "./blocks/sponsors/sponsors-component";
import SyncedSponsors from "./blocks/sponsors/synced-sponsors-component";

const selections: Record<BlockCategory, BlockSelection[]> = {
  header: [
    {
      displayName: "selection-menu.selection.blocks.standalone-navigation",
      previewImage: standaloneNavigationPreview,
      Component: ({ query }) => (
        <StandaloneNavigation>
          {getInitialItemsOrDefault(
            getCurrentSelectionLinks(getTopLevelNodeTrees(query)),
            10,
            DEFAULT_NAVIGATION_LINKS
          ).map(({ label, action }) => (
            <Button
              isLink
              key={label}
              text={label}
              action={action}
              noCustomization
            />
          ))}
        </StandaloneNavigation>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.header-1",
      previewImage: headerOption1Preview,
      Component: () => (
        <HeaderOption1
          backgroundImage="/canvas/hazel/images/background.d3b1da1a.jpg"
          isLogoShowing
          logoSize={50}
          imageFit="cover"
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.header-2",
      previewImage: headerOption2Preview,
      Component: () => (
        <HeaderOption2
          backgroundImage="/canvas/hazel/images/background.08cbe7e2.jpg"
          isLogoShowing
          logoSize={50}
          imageFit="cover"
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.header-3",
      previewImage: headerOption3Preview,
      Component: () => {
        return (
          <HeaderOption3
            backgroundImage="/canvas/hazel/images/background.eb87fb43.jpg"
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
  ],
  sections: [
    {
      displayName: "selection-menu.selection.blocks.rich-text",
      previewImage: richTextPreview,
      Component: () => <RichTextSection />,
    },
    {
      displayName: "selection-menu.selection.blocks.related-events",
      previewImage: relateEventsPreview,
      Component: () => <RelatedEventsSection />,
      selectable: ({ flags, eventSeries }) =>
        flags.eventSeries &&
        Array.isArray(eventSeries) &&
        eventSeries.length > 0,
      previewImageContainerStyle: {
        height: "124px",
        backgroundColor: "#F8FAF4",
        paddingTop: "32px",
      },
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture",
      previewImage: paragraphPicturePreview,
      Component: () => (
        <ParagraphPicture>
          <Image src="/canvas/hazel/images/paragraph-picture-placeholder.bf5eba82.jpg" />
          <ParagraphItem />
        </ParagraphPicture>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture-cta",
      previewImage: paragraphPictureCTAPreview,
      Component: () => (
        <ParagraphPicture>
          <Image src="/canvas/hazel/images/paragraph-picture-placeholder.bf5eba82.jpg" />
          <ParagraphItem showCTA />
        </ParagraphPicture>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.about-details",
      previewImage: aboutDetailsPreview,
      Component: () => <AboutDetails />,
    },
    {
      displayName: "selection-menu.selection.blocks.divider-picture",
      previewImage: dividerPicturePreview,
      Component: () => (
        <DividerPicture backgroundImage="/canvas/hazel/images/divider-picture-placeholder.a6e28f04.jpg" />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.benefits",
      previewImage: benefitsPreview,
      Component: () => (
        <Benefits>
          {DEFAULT_BENEFIT_CARDS.map((item, i) => (
            <BenefitCard key={i}>
              <Image src={item.icon} />
              <Text text={item.title} pattern="headingThree" />
              <Text text={item.description} pattern="description" />
            </BenefitCard>
          ))}
        </Benefits>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.gallery",
      previewImage: galleryPreview,
      Component: () => (
        <Gallery>
          <Image src="/canvas/shared/images/sample-1.971c7ea0.jpg" />
          <Image src="/canvas/shared/images/sample-2.f08a4422.jpg" />
          <Image src="/canvas/shared/images/sample-3.4061fb7d.jpg" />
          <Image src="/canvas/shared/images/sample-4.7365ade5.jpg" />
          <Image src="/canvas/shared/images/sample-5.32bfda46.jpg" />
        </Gallery>
      ),
    },
  ],
  schedule: [
    {
      displayName: "selection-menu.selection.blocks.synced-schedule-highlights",
      previewImage: schedulePreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedDefaultSchedule />,
    },
    {
      displayName: "selection-menu.selection.blocks.schedule-highlights",
      previewImage: schedulePreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <DefaultSchedule
          schedules={
            options.schedules.length > 0 ? options.schedules : DEFAULT_SCHEDULES
          }
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.embedded-schedule",
      previewImage: embeddedSchedulePreview,
      Component: (options) => <EmbeddedSchedule slug={options.event.slug} />,
      deprecated: true,
      selectable: () => false,
    },
  ],
  speakers: [
    {
      displayName: "selection-menu.selection.blocks.synced-speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSpeakers />,
    },
    {
      displayName: "selection-menu.selection.blocks.speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Speakers>
          {getInitialItemsOrDefault<Speaker>(
            options.speakers,
            8,
            DEFAULT_SPEAKERS
          ).map((speaker) => (
            <SpeakerCard
              key={speaker.id}
              speaker={speaker}
              isBioAvailable={!!speaker.bio}
            />
          ))}
        </Speakers>
      ),
    },
  ],
  sponsors: [
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.gold || "Golden",
      }),
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSponsors layout="golden" />,
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.silver || "Silver",
      }),
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSponsors layout="silver" />,
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.bronze || "Bronze",
      }),
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSponsors layout="bronze" />,
    },
    ...sponsorWidgetList(SyncedSponsors),
    {
      displayName: "selection-menu.selection.blocks.synced-sponsors-no-tier",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED_EXT,
      Component: () => <SyncedSponsors layout="no_tier" />,
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-golden",
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors layout="golden">
          {getInitialItemsOrDefault(
            options.sponsors,
            3,
            DEFAULT_GOLDEN_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-silver",
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors layout="silver">
          {getInitialItemsOrDefault(
            options.sponsors,
            4,
            DEFAULT_SILVER_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-bronze",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors layout="bronze">
          {getInitialItemsOrDefault(
            options.sponsors,
            6,
            DEFAULT_BRONZE_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
  ],
  expo: [
    {
      displayName: "selection-menu.selection.blocks.synced-booths-grid",
      previewImage: expoPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedBooths />,
    },
    {
      displayName: "selection-menu.selection.blocks.booths-grid",
      previewImage: expoPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Booths>
          {getInitialItemsOrDefault<Vendor>(
            options.vendors,
            6,
            DEFAULT_VENDORS
          ).map((vendor) => (
            <BoothCard key={vendor.id} vendor={vendor} />
          ))}
        </Booths>
      ),
    },
  ],
  media: [
    {
      displayName: "selection-menu.selection.blocks.video-paragraph",
      previewImage: videoWithParagraphPreview,
      Component: () => (
        <VideoWithParagraph>
          <ParagraphItem showCTA />
          <Video url="https://youtu.be/9m4oFnBLTos" />
        </VideoWithParagraph>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.video-title",
      previewImage: videoWithTitlePreview,
      Component: () => (
        <VideoWithTitle videoAreaSize={VIDEO_AREA_SIZE_SMALL_TYPE} />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.video-grid",
      previewImage: VideoGridPreview,
      Component: () => <VideoGrid />,
    },
    {
      displayName: "selection-menu.selection.blocks.iframe",
      previewImage: iframePreview,
      Component: () => <TopLevelIFrame />,
    },
  ],
};

export default selections;
